import { Button, Stack, Typography } from '@compass/components'
import { Menu, MenuItem, MenuTrigger } from '@compass/Menu/Menu'
import { Popover } from '@compass/Popover'
import Link from '@mui/material/Link'
import clsx from 'clsx'
import { DOCS_LOCATION_PII, IS_ON_VERCEL } from 'const'
import { CircleAlertIcon, InfoIcon } from 'lucide-react'
import { IdEventV4 } from 'models/event'
import { useMemo, useState } from 'react'

import styles from './IpLocationMap.module.scss'

export function IpLocationMap({ event }: { event?: IdEventV4 }) {
  const ipInfo = useMemo(() => event?.ip_info, [event?.ip_info])
  const dataSource = useMemo(
    () => ipInfo?.v6?.geolocation ?? ipInfo?.v4?.geolocation,
    [ipInfo?.v4?.geolocation, ipInfo?.v6?.geolocation]
  )

  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  if (event == null) {
    return (
      <div className={clsx('relative mt-2', styles.map)}>
        <span className={styles.placeholder} />
      </div>
    )
  }

  const piiRedacted =
    event.ip_address === '0.0.0.0' ||
    event.ip_info?.v4?.address === '0.0.0.0' ||
    event.ip_info?.v6?.address === '0.0.0.0'

  const locationUnavailable = !dataSource || (dataSource.latitude == null && dataSource.longitude == null)

  if (piiRedacted || locationUnavailable) {
    return (
      <Stack
        direction='column'
        gap={2}
        className='w-full aspect-[2/1] justify-center items-center mt-2 p-4 bg-gray-100 rounded-md shadow-inset-100 text-center'
      >
        <CircleAlertIcon size={16} className='text-gray-600' />
        <Typography size='sm' className='text-gray-800'>
          {piiRedacted ? (
            <>
              Location is not available due to PII settings.{' '}
              <Link href={DOCS_LOCATION_PII} target='_blank' underline='hover'>
                Learn more
              </Link>
            </>
          ) : (
            'Location is not available.'
          )}
        </Typography>
      </Stack>
    )
  }

  if (error) {
    return null
  }

  const pinPoint = [dataSource?.city_name, dataSource?.country_name].filter(Boolean).join(', ')

  return (
    <div className={clsx('relative mt-2', styles.map, loaded && styles.loaded)}>
      <span className={styles.placeholder} />
      {dataSource && dataSource.latitude != null && dataSource.longitude != null ? (
        <img
          loading='lazy'
          src={`/map?lat=${reducePrecision(dataSource.latitude, 2)}&lon=${reducePrecision(dataSource.longitude, 2)}&height=300&width=600`}
          onLoad={() => setLoaded(true)}
          onError={() => {
            if (IS_ON_VERCEL) {
              setError(true)
            }
          }}
          alt={`Map with ${pinPoint} in the center.`}
        />
      ) : null}
      {pinPoint ? (
        <div className='absolute inset-0 flex items-center justify-center p-4 animate-in fade-in'>
          <div className='-translate-y-1/2 flex flex-col items-center w-full' title={pinPoint}>
            <Typography
              size='sm'
              weight='medium'
              className='max-w-full h-7 px-2 py-1 bg-white rounded-md shadow-400 text-center truncate'
            >
              {pinPoint}
            </Typography>
            <svg
              width='12'
              height='6'
              viewBox='0 0 12 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{ marginTop: '-0.5px' }}
            >
              <g>
                <path
                  className='shadow-100'
                  d='M4.93934 5.50991C5.52513 6.16336 6.47487 6.16336 7.06066 5.50991L12 0H0L4.93934 5.50991Z'
                  fill='white'
                />
              </g>
            </svg>
          </div>
        </div>
      ) : null}
      {loaded ? (
        <>
          <div className={clsx(styles.mapboxLogo, 'animate-in fade-in')} />
          <MenuTrigger>
            <Button
              variant='ghost'
              tone='opaque'
              isIcon
              size='xs'
              className='absolute bottom-1 right-1 animate-in fade-in backdrop-blur-lg backdrop-brightness-110'
            >
              <InfoIcon />
            </Button>
            <Popover placement='bottom end' className='min-w-[150px] w-fit'>
              <Menu>
                <MenuItem href='https://www.mapbox.com/about/maps' target='_blank'>
                  © Mapbox
                </MenuItem>
                <MenuItem href='http://www.openstreetmap.org/copyright' target='_blank'>
                  © OpenStreetMap
                </MenuItem>
              </Menu>
            </Popover>
          </MenuTrigger>
        </>
      ) : null}
    </div>
  )
}

/**
 * Reduces the precision of a coordinate (latitude or longitude).
 * @param coord - The coordinate value.
 * @param precision - The number of decimal places to keep (e.g., 2).
 * @returns The coordinate rounded to the specified precision.
 */
function reducePrecision(coord: number, precision: number): number {
  const factor = Math.pow(10, precision)
  return Math.round(coord * factor) / factor
}
